/**
 * Header — fixed transparent navbar that overlays the scroll-video hero.
 *
 * The hero section is light (slate-50) for the first 80% of its scroll, then
 * transitions to dark (slate-950) at the end. The header tracks scroll
 * position and switches its text colors / background accordingly:
 *
 * - Over the LIGHT hero: dark text, no background, no border
 * - Once past the hero: light text, dark backdrop with blur, subtle border
 *
 * The transition is animated via Framer Motion, matching the hero's own
 * background transition timing so the header swap feels intentional.
 */

import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import { Truck } from "lucide-react";

import { Button } from "@/components/ui/Button";
import { Container } from "@/components/ui/Container";
import { useAuth } from "@/hooks/useAuth";
import { cn } from "@/lib/cn";

import { LanguageSwitcher } from "./LanguageSwitcher";

export function Header() {
  const { t } = useTranslation();
  const { isAuthenticated, signOut } = useAuth();

  // The hero is ONE merged ScrollVideoNarrative — h-[700vh] with both videos
  // crossfading inside it. With useScroll's offset ["start start", "end end"],
  // the section travels through 700vh - 100vh = 600vh of scroll. The bg
  // transition's midpoint is at progress 0.90 → scrollY ≈ 540vh ≈ 5.4 viewports.
  const [overLightHero, setOverLightHero] = useState(true);

  useEffect(() => {
    const computeIsOverLight = () => {
      const heroDarkStartPx = window.innerHeight * 5.4;
      setOverLightHero(window.scrollY < heroDarkStartPx);
    };
    computeIsOverLight();
    window.addEventListener("scroll", computeIsOverLight, { passive: true });
    window.addEventListener("resize", computeIsOverLight, { passive: true });
    return () => {
      window.removeEventListener("scroll", computeIsOverLight);
      window.removeEventListener("resize", computeIsOverLight);
    };
  }, []);

  return (
    <motion.header
      initial={false}
      animate={{
        backgroundColor: overLightHero ? "rgba(248, 250, 252, 0)" : "rgba(2, 6, 23, 0.8)",
        borderBottomColor: overLightHero
          ? "rgba(226, 232, 240, 0)"
          : "rgba(30, 41, 59, 0.6)",
      }}
      transition={{ duration: 0.35, ease: "easeOut" }}
      className="fixed inset-x-0 top-0 z-50 border-b backdrop-blur"
    >
      <Container className="flex h-16 items-center justify-between gap-6">
        <Link
          to="/"
          className={cn(
            "flex items-center gap-2 transition-colors",
            overLightHero ? "text-slate-900" : "text-white",
          )}
        >
          <Truck
            className={cn(
              "h-7 w-7 transition-colors",
              overLightHero ? "text-slate-900" : "text-brand-400",
            )}
            strokeWidth={1.75}
            aria-hidden
          />
          <span className="text-xl font-bold tracking-tight" translate="no">
            {t("app.name")}
          </span>
        </Link>

        <nav className="hidden items-center gap-8 md:flex">
          <a
            href="#for-deg"
            className={cn(
              "text-sm transition-colors",
              overLightHero
                ? "text-slate-600 hover:text-slate-900"
                : "text-slate-300 hover:text-white",
            )}
          >
            {t("nav.howItWorks")}
          </a>
          <a
            href="#kategorier"
            className={cn(
              "text-sm transition-colors",
              overLightHero
                ? "text-slate-600 hover:text-slate-900"
                : "text-slate-300 hover:text-white",
            )}
          >
            {t("nav.categories")}
          </a>
          <a
            href="#om-vegr"
            className={cn(
              "text-sm transition-colors",
              overLightHero
                ? "text-slate-600 hover:text-slate-900"
                : "text-slate-300 hover:text-white",
            )}
          >
            {t("nav.about")}
          </a>
          <a
            href="#bli-en-av-de-forste"
            className={cn(
              "text-sm transition-colors",
              overLightHero
                ? "text-slate-600 hover:text-slate-900"
                : "text-slate-300 hover:text-white",
            )}
          >
            {t("nav.contact")}
          </a>
        </nav>

        <div className="flex items-center gap-3">
          <LanguageSwitcher dark={overLightHero} />
          {isAuthenticated ? (
            <Button
              variant={overLightHero ? "secondary" : "ghost"}
              size="sm"
              onClick={() => void signOut()}
            >
              {t("nav.logout")}
            </Button>
          ) : (
            <Button
              size="sm"
              onClick={() =>
                window.dispatchEvent(new CustomEvent("vegr:open-early-access"))
              }
            >
              {t("nav.earlyAccess")}
            </Button>
          )}
        </div>
      </Container>
    </motion.header>
  );
}
